import {
  Button,
  CheckboxImage,
  CheckboxWrapper,
  Skeleton,
} from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo } from 'react';

import { useCalculateOrderPricing } from '../../../hooks/useCalculateOrderPricing';
import { CollectionSetProduct } from '../../../types/Purchasing';
import { Image } from '../../Image';
import { ProductPrice } from '../../ProductPrice/ProductPrice';
import EmbeddedContainer from '../DrawerContainer/EmbeddedContainer';

const ReleasesFormCompositeImage = (
  images: Array<{ url: string; alt: string }>,
) => {
  // If there are more than 9 images, rendering this composite preview defeats its
  // purpose as it will be too large compared to the thumbnails below it.
  if (images.length > 9) {
    return null;
  }

  return (
    <div className="grid aspect-square items-center rounded-sm bg-[#F3F3F3] p-3">
      <div className="flex flex-wrap justify-center">
        {images.map(({ alt, url }) => (
          <div
            key={url}
            className={classNames({
              'basis-full': images.length === 1,
              'basis-1/2': images.length >= 2 && images.length <= 4,
              'basis-1/3': images.length > 4,
            })}
          >
            <Image alt={alt} src={url} width={384} />
          </div>
        ))}
      </div>
    </div>
  );
};

const CollectionSetForm = ({
  description,
  items,
  onSubmit,
  selectAll,
  toggleItem,
}: {
  description: string | null;
  items: Array<CollectionSetProduct>;
  onSubmit?: () => void;
  selectAll: () => void;
  toggleItem: (id: string) => void;
}) => {
  const { t } = useTranslation(['product']);

  const [emitter] = useAnalyticsEmitter();

  useEffect(() => {
    emitter('product selector', 'viewed');
  }, []);

  const images = items
    .filter(({ selected }) => selected)
    .map(({ image, title }) => ({
      url: image,
      alt: title,
    }));

  const calculationItems = useMemo(() => {
    return items
      .filter(({ selected }) => selected)
      .map((item) => ({
        variantId: item.shopifyVariantId,
        quantity: item.quantity,
      }));
  }, [items]);

  const { calculation: orderPriceCalculation, loading: orderPriceLoading } =
    useCalculateOrderPricing({
      items: calculationItems,
    });

  return (
    <EmbeddedContainer>
      <div className="flex flex-col gap-6">
        <p className="hidden text-md font-bold md:block md:text-lg">
          {t('drawers.review.releases.title')}
        </p>

        {description ? <p>{description}</p> : null}

        {ReleasesFormCompositeImage(images)}

        <div className="flex flex-col gap-4">
          <div className="flex justify-between">
            <p className="font-bold">
              {items.length} {t('drawers.review.releases.variants')}
            </p>
            <button className="hyperlink" onClick={selectAll} type="button">
              {t('drawers.review.releases.selectAll')}
            </button>
          </div>
          <CheckboxWrapper hideLabel label="Select works">
            <div
              className={classNames(
                'grid gap-3',
                items.length === 2 ? 'grid-cols-2' : 'grid-cols-3',
              )}
            >
              {items.map((release) => (
                <CheckboxImage
                  key={release.shopifyId}
                  checked={release.selected}
                  disabled={release.initial || orderPriceLoading}
                  id={release.shopifyId}
                  label={release.title}
                  name={release.title}
                  onChange={() => toggleItem(release.shopifyId)}
                  thumbnail={{
                    alt: release.title,
                    filename: release.image,
                  }}
                  type="checkbox"
                  value={release.shopifyId}
                />
              ))}
            </div>
          </CheckboxWrapper>
        </div>

        {orderPriceLoading || !orderPriceCalculation ? (
          <Skeleton className="h-5 w-full" />
        ) : (
          <div className={classNames('flex w-full gap-x-3')}>
            <span className="flex-1">
              {calculationItems.length > 1
                ? `${t('drawers.review.releases.setOf')} ${
                    calculationItems.length
                  }`
                : t('drawers.review.releases.single')}{' '}
              {orderPriceCalculation.totalDiscount > 0
                ? t('drawers.review.releases.inclDiscount')
                : null}
            </span>
            <span className={classNames('shrink-0')}>
              <ProductPrice
                inMinorUnit
                price={
                  orderPriceCalculation.taxType === 'VAT'
                    ? orderPriceCalculation.totalPrice
                    : orderPriceCalculation.subtotalItemsPrice
                }
                skipConvertCurrency
                skipTaxCalculation
              />
            </span>
          </div>
        )}

        {onSubmit ? (
          <Button
            fullWidth
            label={t('drawers.review.releases.buttonLabel')}
            onClick={() => {
              emitter('product selector', 'submit', {
                'products selected list': items
                  .filter(({ selected }) => selected)
                  .map(({ title }) => title),
                'products selected': items.filter(({ selected }) => selected)
                  .length,
              });
              onSubmit();
            }}
            type="submit"
            variant="primary"
          />
        ) : null}
      </div>
    </EmbeddedContainer>
  );
};

export default CollectionSetForm;
